import { FC, useEffect, useState } from 'react'
import { enqueueSnackbar } from 'notistack'

import { streamingRestApi } from '@/service/streamingApi'
import { GetSport } from '@/types/streams/sport'
import { SportBaseObject } from '@/types/streams/sportBaseObject.types'

import { SelectBox } from '../SelectBox'

export const SPORT_SELECT_TEST_ID = 'sport-select'
export const SPORT_SELECT_LABEL = 'Sport'
export const PROPERTY_SELECT_TEST_ID = 'property-select'
export const PROPERTY_SELECT_LABEL = 'Property'

type SportPropertySelectProps = {
  label?: string
  testId?: string
  required?: boolean
  readOnly?: boolean
  value: {
    sport?: SportBaseObject | null
    property?: SportBaseObject | null
  }
  onChange: {
    sport?: (value: SportBaseObject | null) => void
    property?: (value: SportBaseObject | null) => void
  }
  onClear?: {
    sport?: () => void
    property?: () => void
  }
  disabled?: {
    sport?: boolean
    property?: boolean
  }
}

export const SportPropertySelect: FC<SportPropertySelectProps> = ({
  value: { sport, property },
  onChange: { sport: onSportChange, property: onPropertyChange },
  onClear: { sport: onSportClear, property: onPropertyClear } = {},
  disabled: { sport: disabledSport = false, property: disabledProperty = false } = {},
  label = SPORT_SELECT_LABEL,
  testId = SPORT_SELECT_TEST_ID,
  required,
  readOnly
}) => {
  const sportsApi = streamingRestApi.endpoints.sports
  const [sportsData, setSportData] = useState<GetSport[]>([])

  useEffect(() => {
    if (readOnly) {
      return
    }

    const ac = new AbortController()

    sportsApi
      .getSports({
        signal: ac.signal
      })
      .then((sports) => {
        setSportData(sports)
      })
      .catch((err) => {
        if (err.name === 'CanceledError') {
          return
        }
        enqueueSnackbar('Error fetching sports: ' + String(err), {
          variant: 'error'
        })
      })

    return () => {
      ac.abort()
    }
  }, [])

  const selectedSport = sportsData.find((s) => s.id === sport?.id)
  const getOptionId = (option: SportBaseObject) => String(option.id)
  const formatListOption = (option: SportBaseObject) => option.name
  const formatInputOption = (option: SportBaseObject) => option.name

  if (readOnly) {
    return (
      <>
        {sport && (
          <SelectBox
            label={label}
            testId={testId}
            value={sport}
            options={[sport]}
            getOptionId={getOptionId}
            formatListOption={formatListOption}
            formatInputOption={formatInputOption}
            disabled
            required={required}
            clearable
            fullWidth
          />
        )}
        {property && (
          <SelectBox
            testId={PROPERTY_SELECT_TEST_ID}
            label={PROPERTY_SELECT_LABEL}
            value={property}
            options={[property]}
            getOptionId={getOptionId}
            formatListOption={formatListOption}
            formatInputOption={formatInputOption}
            disabled
            required={required}
            clearable
            fullWidth
          />
        )}
      </>
    )
  }

  return (
    <>
      {!!onSportChange && (
        <SelectBox
          label={label}
          testId={testId}
          value={sport || null}
          options={sportsData}
          getOptionId={getOptionId}
          formatListOption={formatListOption}
          formatInputOption={formatInputOption}
          onChange={onSportChange}
          onClear={onSportClear}
          disabled={disabledSport}
          clearable
          fullWidth
          required={required}
        />
      )}
      {!!onPropertyChange && (
        <SelectBox
          testId={PROPERTY_SELECT_TEST_ID}
          label={PROPERTY_SELECT_LABEL}
          value={property || null}
          options={selectedSport?.properties || []}
          getOptionId={getOptionId}
          formatListOption={formatListOption}
          formatInputOption={formatInputOption}
          onChange={onPropertyChange}
          onClear={onPropertyClear}
          disabled={!sport || disabledProperty}
          clearable
          fullWidth
          required={required}
        />
      )}
    </>
  )
}
